import Select, { StylesConfig, components, OptionProps } from 'react-select'
import { useController, UseControllerProps } from 'react-hook-form'
import cx from 'classnames'
import Checkbox from './Checkbox'

type Option = Array<{ label: string; value: string | number }>

type DropdownProps = UseControllerProps & {
  id: string
  label?: string
  options: Option
  labelClassName?: string
}

const customStyles: StylesConfig = {
  menu: (provided) => ({
    ...provided,
    marginTop: '-38px',
    boxShadow: 'unset',
    border: 'solid 1px #7D7D7D',
    borderRight: 'solid 1px black',
    borderBottom: 'solid 1px black',
    borderRadius: '5px',
    position: 'absolute',
    zIndex: 10,
  }),

  control: (provided) => ({
    ...provided,
    borderRadius: '5px',
    borderColor: '#7D7D7D',
    boxShadow: 'unset',
    height: '33px',
    '&:hover': {
      borderColor: '#7D7D7D',
    },
  }),

  option: (provided, state) => ({
    ...provided,
    ...(!state.isSelected && {
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
      },
    }),
    ...(state.isSelected && {
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
      },
    }),
    '&:active': {
      backgroundColor: 'rgba(0, 0, 0, 0.05) !important',
    },
  }),

  valueContainer: (provided) => ({
    ...provided,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    flexWrap: 'nowrap',
  }),
}

const CustomOption = (props: OptionProps) => {
  return (
    <>
      <components.Option {...props}>
        <Checkbox value={props.isSelected} label={props.label} disabled />
      </components.Option>
    </>
  )
}

function Dropdown(props: DropdownProps) {
  const { options, label, id, labelClassName, ...controllerProps } = props
  const {
    field: { onChange, value, ...rest },
  } = useController(controllerProps)
  return (
    <div className="mb-8">
      <div className="mb-4 flex items-center justify-between gap-2">
        {label && (
          <span className={cx('dropdown-label', labelClassName)}>
            {label}
            {value && value.length > 0 ? `(${value?.length})` : null}
          </span>
        )}
        <button
          className="dropdown-clear"
          onClick={() => onChange([])}
          disabled={!value?.length}
        >
          Clear
        </button>
      </div>
      <Select
        {...rest}
        instanceId={id}
        options={options}
        isSearchable={false}
        className="mb-8 w-full font-inter"
        placeholder="Select..."
        styles={customStyles}
        onChange={(e) => {
          const currentValue = e as Option
          onChange(currentValue.map(({ value }) => value))
        }}
        value={
          Array.isArray(value)
            ? options.filter((v) => value.includes(v.value))
            : null
        }
        isMulti
        components={{ Option: CustomOption }}
        closeMenuOnSelect={false}
        closeMenuOnScroll={false}
        hideSelectedOptions={false}
        isClearable={false}
      />
      <style jsx>{`
        .dropdown-label {
          @apply font-ibm text-[14px] font-semibold uppercase leading-[18.2px] text-black;
        }
        .dropdown-clear {
          @apply font-inter text-[14px] font-semibold leading-[16.94px] text-black underline;
        }
        .dropdown-clear:disabled {
          @apply text-gray-300;
        }
      `}</style>
    </div>
  )
}

export default Dropdown
